import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CalendarCheck, Download, FileText, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const save = (data, name, type) => {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const cash = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const btn = "hard-btn py-2 px-4 font-mono uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-40";
const outline = "border-2 border-ink py-2 px-4 font-mono uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-2 hover:bg-ink hover:text-white disabled:opacity-40";
const input = "w-full px-3 py-2 border-2 border-ink font-mono text-sm";

const W2_BOXES = [
  ["box1", "Box 1 wages"], ["box2", "Box 2 federal withheld"],
  ["box3", "Box 3 SS wages"], ["box4", "Box 4 SS tax"],
  ["box5", "Box 5 Medicare wages"], ["box6", "Box 6 Medicare tax"],
  ["box12d", "Box 12 code D · 401(k)"],
  ["state_wages", "Box 16 state wages"], ["state_tax", "Box 17 state tax"],
];
const NEC_BOXES = [["box1", "Box 1 compensation"], ["box4", "Box 4 federal withheld"],
  ["state_tax", "Box 5 state tax"]];

const pull = async (path, body, name, type, setBusy, label) => {
  setBusy(label);
  try {
    const res = body
      ? await api.post(path, body, { responseType: "blob" })
      : await api.get(path, { responseType: "blob" });
    save(res.data, name, type);
    toast.success(`${label} ready`);
  } catch (e) { toast.error(formatApiError(e)); }
  setBusy("");
};

/* -------------------------------------------------- Corrections (W-2c / 1099) */
export function CorrectionsCard({ year, enabled }) {
  const [kind, setKind] = useState("w2");
  const [rows, setRows] = useState([]);
  const [people, setPeople] = useState([]);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [list, preview] = await Promise.all([
        api.get(`/reports/corrections?year=${year}&kind=${kind}`),
        api.get(`/reports/yearend/preview?year=${year}`),
      ]);
      setRows(list.data.corrections || []);
      setPeople(kind === "w2" ? (preview.data.w2.people || []) : []);
    } catch { setRows([]); }
  }, [year, kind]);

  useEffect(() => { load(); }, [load]);

  const boxes = kind === "w2" ? W2_BOXES : NEC_BOXES;

  const startDraft = () => setDraft({
    employee_id: people[0]?.id || "", correction_type: 1, reason: "", corrected: {},
    previously_reported: {},
  });

  const submit = async () => {
    setBusy("save");
    try {
      await api.post("/reports/corrections", {
        year: Number(year), kind, employee_id: draft.employee_id,
        correction_type: Number(draft.correction_type),
        reason: draft.reason,
        corrected: Object.fromEntries(Object.entries(draft.corrected)
          .filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)])),
        previously_reported: Object.fromEntries(
          Object.entries(draft.previously_reported || {}).filter(([, v]) => v !== "")),
      });
      toast.success("Correction recorded");
      setDraft(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reports/corrections/${id}`);
      toast.success("Correction removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="hard-card p-6 mb-8" data-testid="corrections-card">
      <div className="overline mb-2">§ CORRECTED STATEMENTS · {year}</div>
      <div className="font-heading font-black text-2xl tracking-tight">
        W-2c · W-3c · corrected 1099-NEC
      </div>
      <div className="font-mono text-xs text-muted-foreground mt-1 max-w-2xl">
        A figure that has already gone out is fixed with its own return, carrying both
        what was previously reported and what is correct. Filing is the EFW2C file
        (1024-byte records) for W-2cs and IRIS for 1099s.
      </div>

      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <select value={kind} onChange={(e) => setKind(e.target.value)}
          data-testid="correction-kind" className={`${input} max-w-xs`}>
          <option value="w2" label="Form W-2 (employees)" />
          <option value="1099" label="Form 1099-NEC (contractors)" />
        </select>
        <button onClick={startDraft} disabled={!enabled} data-testid="correction-add"
          className={btn}><Plus className="w-3 h-3" /> Record a correction</button>
      </div>

      {draft && (
        <div className="mt-4 border-2 border-ink p-4" data-testid="correction-form">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <div className="overline mb-2 text-[10px]">Worker</div>
              {kind === "w2" ? (
                <select value={draft.employee_id} data-testid="correction-employee"
                  onChange={(e) => setDraft({ ...draft, employee_id: e.target.value })}
                  className={input}>
                  {people.map((p) => <option key={p.id} value={p.id} label={p.name} />)}
                </select>
              ) : (
                <input value={draft.employee_id} data-testid="correction-employee"
                  onChange={(e) => setDraft({ ...draft, employee_id: e.target.value })}
                  placeholder="Contractor id" className={input} />
              )}
            </div>
            <div>
              <div className="overline mb-2 text-[10px]">What went wrong</div>
              <select value={draft.correction_type} data-testid="correction-type"
                onChange={(e) => setDraft({ ...draft, correction_type: e.target.value })}
                className={input}>
                <option value={1} label="Type 1 — a wrong figure or code" />
                <option value={2} label="Type 2 — wrong TIN or payee name" />
              </select>
            </div>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            {boxes.map(([key, label]) => (              <div key={key}>
                <div className="overline mb-2 text-[10px]">{label}</div>
                <input type="number" step="0.01" min="0" data-testid={`correction-${key}`}
                  value={draft.corrected[key] ?? ""}
                  onChange={(e) => setDraft({
                    ...draft,
                    corrected: { ...draft.corrected, [key]: e.target.value },
                  })}
                  placeholder="leave blank if right" className={input} />
              </div>
            ))}
          </div>
          {Number(draft.correction_type) === 2 && (
            <div className="mt-3 grid gap-3 md:grid-cols-3"
              data-testid="correction-identity-block">
              <div className="md:col-span-3 font-mono text-[10px]">
                What was WRONG on the statement you already filed — the current record
                holds the right details.
              </div>
              {[["ssn", kind === "w2" ? "Previously reported SSN" : "Previously reported TIN"],
                ["first_name", "Previously reported first name"],
                ["last_name", "Previously reported last name"]].map(([key, label]) => (
                <div key={key}>
                  <div className="overline mb-2 text-[10px]">{label}</div>
                  <input data-testid={`correction-prev-${key}`}
                    value={draft.previously_reported?.[key] ?? ""}
                    onChange={(e) => setDraft({
                      ...draft,
                      previously_reported: {
                        ...draft.previously_reported, [key]: e.target.value,
                      },
                    })}
                    placeholder={key === "ssn" ? "9 digits" : ""} className={input} />
                </div>
              ))}
            </div>
          )}
          <div className="mt-3">
            <div className="overline mb-2 text-[10px]">Reason (kept with the record)</div>
            <input value={draft.reason} data-testid="correction-reason"
              onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
              className={input} />
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={submit} disabled={busy === "save"} className={btn}
              data-testid="correction-save">
              {busy === "save" ? "Saving…" : "Record correction"}
            </button>
            <button onClick={() => setDraft(null)} className={outline}
              data-testid="correction-cancel">Cancel</button>
          </div>
        </div>
      )}

      {rows.length > 0 ? (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full font-mono text-xs" data-testid="corrections-table">
            <thead><tr className="border-b-2 border-ink text-left">
              <th className="py-2">Worker</th><th>Type</th><th>Boxes</th>
              <th>Was</th><th>Correct</th><th />
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-ink/20"
                  data-testid={`correction-row-${r.id}`}>
                  <td className="py-2">{r.name || r.employee_id}</td>
                  <td>{r.correction_type}{r.identity_changed ? " · name/TIN" : ""}</td>
                  <td>{(r.fields || []).join(", ") || "—"}</td>
                  <td>{(r.fields || []).map((f) => cash(r.previously_reported[f])).join(" ")}</td>
                  <td>{(r.fields || []).map((f) => cash(r.correct[f])).join(" ")}</td>
                  <td className="text-right">
                    <button onClick={() => remove(r.id)} data-testid={`correction-delete-${r.id}`}
                      className="border-2 border-bad text-bad px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-bad hover:text-white">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-4 font-mono text-xs text-muted-foreground italic"
          data-testid="corrections-empty">
          Nothing recorded for {year}. Statements as filed stand.
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {kind === "w2" ? (
          <>
            <button disabled={!enabled || !rows.length} data-testid="w2c-employee-copies"
              onClick={() => pull("/reports/w2c.pdf", { year: Number(year) },
                `w2c-${year}-employee-copies.pdf`, "application/pdf", setBusy, "W-2c copies")}
              className={btn}><Download className="w-3 h-3" /> W-2c copies B/C/2</button>
            <button disabled={!enabled || !rows.length} data-testid="w2c-record-copies"
              onClick={() => pull("/reports/w2c.pdf", { year: Number(year), copies: "A,D" },
                `w2c-${year}-record.pdf`, "application/pdf", setBusy, "W-2c record set")}
              className={outline}><FileText className="w-3 h-3" /> Record set + W-3c</button>
            <button disabled={!enabled || !rows.length} data-testid="efw2c-download"
              onClick={() => pull(`/reports/efw2c.txt?year=${year}`, null,
                `w2creport-${year}.txt`, "text/plain", setBusy, "EFW2C file")}
              className={btn}><Download className="w-3 h-3" /> EFW2C file for SSA</button>
          </>
        ) : (
          <>
            <button disabled={!enabled || !rows.length} data-testid="nec-corrected-copies"
              onClick={() => pull("/reports/1099nec-corrected.pdf", { year: Number(year) },
                `1099nec-corrected-${year}.pdf`, "application/pdf", setBusy, "Corrected 1099s")}
              className={btn}><Download className="w-3 h-3" /> Corrected recipient copies</button>
            <button disabled={!enabled || !rows.length} data-testid="nec-corrected-record"
              onClick={() => pull("/reports/1099nec-corrected.pdf",
                { year: Number(year), copies: "A" },
                `1099nec-corrected-record-${year}.pdf`, "application/pdf", setBusy, "Record set")}
              className={outline}><FileText className="w-3 h-3" /> Record set + 1096</button>
            <button disabled={!enabled || !rows.length} data-testid="iris-corrections-download"
              onClick={() => pull(`/reports/1099-iris-corrections.csv?year=${year}`, null,
                `iris-corrections-${year}.csv`, "text/csv", setBusy, "IRIS corrections")}
              className={btn}><Download className="w-3 h-3" /> IRIS correction file</button>
          </>
        )}
      </div>
      <div className="mt-3 font-mono text-[10px] border-2 border-ink p-3"
        data-testid="corrections-warning">
        <AlertTriangle className="w-3 h-3 inline mr-1" />
        A type 2 fix produces TWO forms per worker — the bad record zeroed out to void it,
        then a clean replacement. Copy A, the W-3c and the 1096 are record copies only.
      </div>
    </div>
  );
}

/* -------------------------------------------------- State wage upload files */
export function StateWageFileCard({ year, enabled }) {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ state: "", quarter: 4, ui_rate_pct: 0 });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports/state-wage-file/info");
        setInfo(data);
        setForm((f) => ({
          ...f,
          state: data.supported.includes(data.company_state) ? data.company_state
            : data.supported[0],
        }));
      } catch { setInfo(null); }
    })();
  }, []);

  if (!info) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="hard-card p-6 mb-8" data-testid="state-wage-file-card">
      <div className="overline mb-2">§ STATE WAGE UPLOAD FILES</div>
      <div className="font-heading font-black text-2xl tracking-tight">
        NY NYS-45 upload · CA DE 9C (ICESA)
      </div>
      <div className="font-mono text-xs text-muted-foreground mt-1 max-w-3xl"
        data-testid="state-wage-file-note">
        {info.annual_state_w2}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <div>
          <div className="overline mb-2 text-[10px]">State</div>
          <select value={form.state} onChange={set("state")} data-testid="swf-state"
            className={input}>
            {info.supported.map((s) => <option key={s} value={s} label={s} />)}
          </select>
        </div>
        <div>
          <div className="overline mb-2 text-[10px]">Quarter</div>
          <select value={form.quarter} onChange={set("quarter")} data-testid="swf-quarter"
            className={input}>
            {[1, 2, 3, 4].map((q) => <option key={q} value={q} label={`Q${q}`} />)}
          </select>
        </div>
        <div>
          <div className="overline mb-2 text-[10px]">UI rate %</div>
          <input type="number" step="0.001" value={form.ui_rate_pct}
            onChange={set("ui_rate_pct")} data-testid="swf-ui-rate" className={input} />
        </div>
        <div>
          <div className="overline mb-2 text-[10px]">
            {form.state === "NY" ? "UI registration no." : "EDD account no. (SEAN)"}
          </div>
          <input value={form.state === "NY" ? (form.ui_registration || "")
            : (form.account_number || "")}
            onChange={set(form.state === "NY" ? "ui_registration" : "account_number")}
            data-testid="swf-account" className={input} />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <button disabled={!enabled} data-testid="swf-download" className={btn}
          onClick={() => {
            const q = new URLSearchParams({
              year: String(year), quarter: String(form.quarter), state: form.state,
              ui_rate_pct: String(form.ui_rate_pct || 0),
              ui_registration: form.ui_registration || "",
              account_number: form.account_number || "",
            }).toString();
            pull(`/reports/state-wage-file.txt?${q}`, null,
              `${form.state.toLowerCase()}-wage-upload-Q${form.quarter}-${year}.txt`,
              "text/plain", setBusy, "State wage file");
          }}>
          <Download className="w-3 h-3" />
          {busy ? "Building…" : `${form.state} upload file`}
        </button>
        <a href={info.portals[form.state]} target="_blank" rel="noreferrer"
          data-testid="swf-portal" className={outline}>Open the {form.state} portal ↗</a>
      </div>
      <div className="mt-3 font-mono text-[10px] border-2 border-ink p-3"
        data-testid="swf-detail">
        {info.notes[form.state]} Records are{" "}
        {info.record_lengths[form.state]} bytes fixed-width. The file carries full SSNs —
        every export is audited.
      </div>
    </div>
  );
}

/* -------------------------------------------------- January checklist */
export function JanuaryChecklistCard({ year, enabled }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try { setData((await api.get(`/reports/yearend/checklist?year=${year}`)).data); }
      catch { setData(null); }
    })();
  }, [year]);

  if (!data || !data.items.length) return null;
  const tone = { done: "text-good", todo: "", blocked: "text-bad" };

  return (
    <div className="hard-card p-6 mb-8" data-testid="january-checklist-card">
      <div className="overline mb-2">§ JANUARY {data.filing_year} · WHAT IS DUE</div>
      <div className="font-heading font-black text-2xl tracking-tight">
        Year-end checklist for {data.tax_year}
      </div>
      <div className="font-mono text-xs mt-1" data-testid="checklist-counts">
        {data.counts.todo} to do · {data.counts.blocked} blocked · {data.counts.done} already filed
      </div>

      <div className="mt-4 space-y-3">
        {data.items.map((item, i) => (
          <div key={`${item.due_date}-${item.title}`}
            className="border-2 border-ink p-3" data-testid={`checklist-item-${i}`}>
            <div className="flex flex-wrap justify-between gap-2 items-baseline">
              <div className={`font-mono text-[10px] uppercase tracking-widest ${tone[item.status]}`}>
                {item.status}{item.overdue ? " · overdue" : ""}
              </div>
              <div className="font-mono text-[10px]">{item.due_date}</div>
            </div>
            <div className="font-heading font-black text-base mt-1">{item.title}</div>
            <div className="font-mono text-xs text-muted-foreground mt-1">{item.detail}</div>
            <div className="font-mono text-[10px] mt-1 opacity-70">{item.where}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button disabled={!enabled} data-testid="checklist-pdf" className={btn}
          onClick={() => pull(`/reports/yearend/checklist.pdf?year=${year}`, null,
            `january-${data.filing_year}-checklist.pdf`, "application/pdf", setBusy,
            "Checklist")}>
          <CalendarCheck className="w-3 h-3" />
          {busy ? "Building…" : "One-page checklist PDF"}
        </button>
      </div>
      <div className="mt-3 font-mono text-[10px] border-2 border-ink p-3"
        data-testid="checklist-caveat">
        <AlertTriangle className="w-3 h-3 inline mr-1" />{data.caveat}
      </div>
    </div>
  );
}
